/**
 * Audio Element Pool
 * 
 * Maintains a small pool of pre-warmed HTMLMediaElements so track transitions
 * avoid cold-start rebuffering. Elements are recycled — when released they are
 * reset (src cleared, events stripped) and returned to the pool for the next
 * acquire() call.
 *
 * **Listener safety** — every acquire() mints a fresh AbortController whose
 * signal should be passed to every `addEventListener` on that element.  When
 * the element is released (or re-acquired) the controller is aborted,
 * guaranteeing *all* handlers are removed in one shot — no stale listeners
 * can survive across tracks.
 *
 * Pool slots:
 *   - AUDIO slots (for .mp3 / .ogg / .wav etc.)
 *   - VIDEO slots (for .mp4 containers — some browsers need <video> for these)
 *
 * Because mobile browsers limit simultaneous MediaElement instances (iOS Safari
 * caps around 4-8), the pool is kept intentionally small.
 */

const AUDIO_POOL_SIZE = 3;
const VIDEO_POOL_SIZE = 2;

interface PoolEntry {
  el: HTMLAudioElement; // may actually be a <video> cast to HTMLAudioElement
  kind: "audio" | "video";
  inUse: boolean;
}

const pool: PoolEntry[] = [];
let warmedUp = false;

// Hand the live pool array to the debug module (no-op if debug isn't loaded yet)
import { _registerPool } from "@/lib/poolDebug";
_registerPool(pool);

/**
 * WeakMap from element → its current AbortController.
 * Using a WeakMap avoids memory leaks for overflow elements that leave the pool.
 */
const controllers = new WeakMap<HTMLMediaElement, AbortController>();

/** Detect whether a source needs a <video> element */
function needsVideo(src: string): boolean {
  return /\.mp4(\?|$)/i.test(src);
}

/** Create a raw media element of the given kind */
function createElement(kind: "audio" | "video"): HTMLAudioElement {
  if (kind === "video") {
    const v = document.createElement("video");
    v.playsInline = true;
    v.style.display = "none";
    document.body.appendChild(v);
    return v as unknown as HTMLAudioElement;
  }
  return new Audio();
}

/**
 * Abort the current controller (removing every listener that was attached with
 * its signal) and mint a fresh one for the next lifecycle.
 */
function rotateController(el: HTMLMediaElement): AbortController {
  const prev = controllers.get(el);
  if (prev) {
    try { prev.abort(); } catch { /* ignore */ }
  }
  const next = new AbortController();
  controllers.set(el, next);
  return next;
}

/** Strip all event listeners by aborting the controller + resetting element state */
function resetElement(entry: PoolEntry): void {
  const el = entry.el;
  el.pause();
  // Abort all listeners attached via the signal
  rotateController(el);
  // Suppress error event from blank src
  el.onerror = null;
  el.removeAttribute("src");
  try { el.load(); } catch { /* ignore */ }
  el.volume = 1;
  el.currentTime = 0;
  el.preload = "auto";
}

/**
 * Warm up the pool by pre-creating elements.
 * Call once after the first user gesture so mobile browsers allow creation.
 */
export function warmPool(): void {
  if (warmedUp) return;
  warmedUp = true;

  for (let i = 0; i < AUDIO_POOL_SIZE; i++) {
    pool.push({ el: createElement("audio"), kind: "audio", inUse: false });
  }
  for (let i = 0; i < VIDEO_POOL_SIZE; i++) {
    pool.push({ el: createElement("video"), kind: "video", inUse: false });
  }
}

/**
 * Acquire a media element for the given source URL.
 * Prefers a pooled element of the right kind; creates a new one if none free.
 *
 * Every acquisition mints a fresh AbortController — retrieve it with
 * `getSignal(el)` and pass it as `{ signal }` to addEventListener.
 */
export function acquire(src: string): HTMLAudioElement {
  const kind = needsVideo(src) ? "video" : "audio";

  // Find a free slot of the right kind
  let entry = pool.find((e) => !e.inUse && e.kind === kind);

  if (!entry) {
    // No free slot — create an overflow element (will be destroyed on release)
    const el = createElement(kind);
    entry = { el, kind, inUse: true };
    // Don't add to pool — it'll be cleaned up on release
  } else {
    entry.inUse = true;
  }

  // Rotate controller so any lingering listeners from a previous cycle are gone
  rotateController(entry.el);

  entry.el.src = src;
  entry.el.preload = "auto";
  return entry.el;
}

/**
 * Get the AbortSignal for an element's current lifecycle.
 * Pass this as `{ signal }` to addEventListener so listeners auto-remove on release.
 */
export function getSignal(el: HTMLMediaElement): AbortSignal {
  let ac = controllers.get(el);
  if (!ac) {
    ac = new AbortController();
    controllers.set(el, ac);
  }
  return ac.signal;
}

/**
 * Release a media element back to the pool.
 * If it's a pooled element it gets reset and marked available.
 * If it's an overflow element it gets fully destroyed.
 */
export function release(el: HTMLMediaElement): void {
  const entry = pool.find((e) => e.el === el);

  if (entry) {
    resetElement(entry);
    entry.inUse = false;
  } else {
    // Overflow element — destroy it
    el.pause();
    // Abort all listeners
    rotateController(el);
    el.onerror = null;
    el.removeAttribute("src");
    try { el.load(); } catch { /* ignore */ }
    if (el instanceof HTMLVideoElement && el.parentNode) {
      el.parentNode.removeChild(el);
    }
  }
}

/**
 * Drain the entire pool — call on unmount if needed.
 */
export function drainPool(): void {
  for (const entry of pool) {
    entry.el.pause();
    rotateController(entry.el);
    entry.el.onerror = null;
    entry.el.removeAttribute("src");
    try { entry.el.load(); } catch { /* ignore */ }
    if (entry.el instanceof HTMLVideoElement && (entry.el as unknown as HTMLVideoElement).parentNode) {
      ((entry.el as unknown as HTMLVideoElement).parentNode as Node).removeChild(entry.el as unknown as HTMLVideoElement);
    }
  }
  pool.length = 0;
  warmedUp = false;
}
